import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardTileType, KanbanBoardStore } from '@store';
import { AggregatedStatus, GenericDataResponse } from '@vi-iot/client';
import { DashboardApiMocks } from '@vi-iot/client/apis/dashboard/dashboard.mocks';
import { isArray } from 'lodash-es';
import { forkJoin, Observable, of, OperatorFunction } from 'rxjs';
import { delay, map, switchMap, take } from 'rxjs/operators';
import {
  ButtonFilterData,
  ButtonFilterDataName,
  DashboardActiveAnomaliesEnum,
  DashboardAddColumnPayload,
  DashboardApiModel,
  DashboardColumn,
  DashboardColumnDataWithoutInstallationDetails,
  DashboardEditColumnPayload,
  DashboardFilterPayload,
  DashboardFilterResponse,
  DashboardInstallationData,
  DashboardInstallationListResponse,
  DashboardInstallationsFilterNames,
  DashboardInstallationsFilterPayload,
  DashboardInstallationsPayload,
  DashboardInstallationsStatusEnum,
  DashboardInstallationsSumsPayload,
  DashboardInstallationsSumsResponse,
  DashboardInstallationTimeBasedEnum,
  GenericSumsFilterDataPayload,
  GetColumnInstallationsPayload,
  MAXIMUM_DASHBOARD_LIMIT,
  SortOrder,
} from './dashboard.api-model';
import demoInstallations = DashboardApiMocks.demoInstallations;
import installationsWithAnomalies = DashboardApiMocks.installationsWithAnomalies;
import installationsWithoutHEMSIds = DashboardApiMocks.installationsWithoutHEMSIds;
import installationsWithoutSRCIds = DashboardApiMocks.installationsWithoutSRCIds;
import installationsWithPaidPackages = DashboardApiMocks.installationsWithPaidPackages;
import lockedInstallations = DashboardApiMocks.lockedInstallations;

@Injectable({ providedIn: 'root' })
export class DashboardMockApi implements DashboardApiModel {
  constructor(private router: Router, private kanbanBoardStore: KanbanBoardStore) {}

  getDashboard(): Observable<GenericDataResponse<DashboardColumn[]>> {
    if (this.router.url.includes('dashboard')) {
      return this.getDashboardMockForLandingPage().pipe(delay(500));
    }
    return of({ data: DashboardApiMocks.getMockedDashboardColumns() }).pipe(delay(500));
  }

  recreateSnapshot(): Observable<GenericDataResponse<DashboardColumn[]>> {
    if (this.router.url.includes('dashboard')) {
      return this.getDashboardMockForLandingPage().pipe(delay(500));
    }
    return of({ data: DashboardApiMocks.getMockedDashboardColumns() }).pipe(delay(500));
  }

  getFilters(): Observable<GenericDataResponse<DashboardFilterResponse[]>> {
    return of({ data: DashboardApiMocks.mockedFilters }).pipe(delay(500));
  }

  setFilters(filters: DashboardFilterPayload): Observable<GenericDataResponse<DashboardColumn[]>> {
    const filteredInstallations = DashboardApiMocks.applyFilters(filters);
    return of({ data: DashboardApiMocks.getMockedDashboardColumns(filteredInstallations) }).pipe(
      delay(500)
    );
  }

  addColumn(_column: DashboardAddColumnPayload): Observable<GenericDataResponse<DashboardColumn>> {
    return of({ data: DashboardApiMocks.newColumnMock(_column) }).pipe(delay(500));
  }

  deleteColumn(_id: number, _version: string): Observable<GenericDataResponse<null>> {
    return of(null);
  }

  editColumn(
    id: number,
    body: DashboardEditColumnPayload
  ): Observable<GenericDataResponse<DashboardColumnDataWithoutInstallationDetails>> {
    // self defined column
    if (body.installations) {
      return of({ data: DashboardApiMocks.editedColumnMock(body, id) }).pipe(delay(500));
    } else {
      // fixed column - sort order change
      return this.kanbanBoardStore.columns$.pipe(
        switchMap((columns) => {
          const columnToEdit = columns.find((column) => column.id === id);
          const response: DashboardColumnDataWithoutInstallationDetails = {
            ...DashboardApiMocks.editedColumnMock(body, id),
            installations: structuredClone(columnToEdit.installationIds).reverse(),
          };

          return of({ data: response }).pipe(delay(500));
        }),
        take(1)
      );
    }
  }

  fetchInstallationForColumn(
    data: GetColumnInstallationsPayload,
    _filters: DashboardFilterPayload,
    sortOrder: SortOrder
  ): Observable<GenericDataResponse<DashboardInstallationData[]>> {
    return this.kanbanBoardStore.columns$.pipe(
      switchMap((columns) => {
        const column = columns.find((column) => column.id === data.columnId);
        const installations =
          sortOrder === SortOrder.asc
            ? column.installations
            : structuredClone(column.installations).reverse();

        return of({ data: installations }).pipe(delay(500));
      }),
      take(1)
    );
  }

  moveKanbanInstallation(
    _body: [
      DashboardColumnDataWithoutInstallationDetails,
      DashboardColumnDataWithoutInstallationDetails
    ]
  ): Observable<
    GenericDataResponse<
      [DashboardColumnDataWithoutInstallationDetails, DashboardColumnDataWithoutInstallationDetails]
    >
  > {
    const response = (<unknown>{
      data: [],
    }) as GenericDataResponse<
      [DashboardColumnDataWithoutInstallationDetails, DashboardColumnDataWithoutInstallationDetails]
    >;

    return of(response);
  }

  getFilteredInstallationsV1(
    offset: number,
    limit: number,
    body: DashboardFilterPayload,
    _withQuickView = true
  ): Observable<GenericDataResponse<DashboardInstallationData[]>> {
    return of({})
      .pipe(
        map(
          (): GenericDataResponse<DashboardInstallationData[]> => {
            return { data: DashboardApiMocks.applyFilters(body).slice(offset, offset + limit) };
          }
        ),
        take(1)
      )
      .pipe(delay(800));
  }

  getFilteredInstallationsV2(
    limit: number,
    body: DashboardFilterPayload,
    cursor?: string
  ): Observable<DashboardInstallationListResponse> {
    return this.kanbanBoardStore.installationListState$
      .pipe(
        map(
          (state): DashboardInstallationListResponse => {
            let offset = 0;
            if (cursor) {
              offset = state.value.length;
            }

            const outputCursor =
              DashboardApiMocks.applyFilters(body).length > offset + limit
                ? 'cursor allowing fetching'
                : '';

            return {
              data: DashboardApiMocks.applyFilters(body).slice(offset, offset + limit),
              cursor: { next: outputCursor },
            } as DashboardInstallationListResponse;
          }
        ),
        take(1)
      )
      .pipe(delay(800));
  }

  // Very unlikely but possible - handle case when installation reach more than 1k invitations
  fetchInvitationsRecursively(
    filter: DashboardFilterPayload,
    limit = MAXIMUM_DASHBOARD_LIMIT
  ): OperatorFunction<DashboardInstallationListResponse, DashboardInstallationData[]> {
    return (
      inputObservable: Observable<DashboardInstallationListResponse>
    ): Observable<DashboardInstallationData[]> =>
      inputObservable.pipe(
        switchMap((res) => {
          if (res?.cursor?.next) {
            return this.getFilteredInstallationsV2(limit, filter, res.cursor.next).pipe(
              switchMap((nextRes) =>
                forkJoin([
                  of(res.data),
                  of(nextRes).pipe(this.fetchInvitationsRecursively(filter)),
                ]).pipe(
                  map(([previousRequest, latestRequest]) => [...previousRequest, ...latestRequest])
                )
              )
            );
          }

          // latestRequest
          return of(res.data);
        })
      );
  }

  fetchInstallations(
    data: DashboardInstallationsPayload
  ): Observable<GenericDataResponse<DashboardInstallationData[]>> {
    if (data.filter.filterName === DashboardInstallationsFilterNames.AggregatedStatus) {
      return this.fetchInstallationsBasedOnStatus(data.filter).pipe(delay(800));
    }

    if (data.filter.filterName === DashboardInstallationsFilterNames.RecentlyConnected) {
      if (
        data.filter.values === DashboardInstallationTimeBasedEnum.Today ||
        data.filter.values === DashboardInstallationTimeBasedEnum.Yesterday
      ) {
        return of({ data: [] });
      } else {
        const offlineInstallations: DashboardInstallationData[] = demoInstallations.filter(
          (installation: DashboardInstallationData): boolean =>
            installation.aggregatedStatus === AggregatedStatus.Offline
        );

        return of({ data: offlineInstallations }).pipe(delay(800));
      }
    }

    if (data.filter.filterName === DashboardInstallationsFilterNames.HemsNotActivated) {
      const installationsWithoutHEMS: DashboardInstallationData[] = demoInstallations.filter(
        (installation: DashboardInstallationData) =>
          installationsWithoutHEMSIds.some((id: number) => installation.id === id)
      );
      return of({ data: installationsWithoutHEMS }).pipe(delay(800));
    }

    if (data.filter.filterName === DashboardInstallationsFilterNames.SrcNotActivated) {
      const installationsWithoutHEMS: DashboardInstallationData[] = demoInstallations.filter(
        (installation: DashboardInstallationData) =>
          installationsWithoutSRCIds.some((id: number) => installation.id === id)
      );
      return of({ data: installationsWithoutHEMS }).pipe(delay(800));
    }

    if (
      data.filter.filterName === DashboardInstallationsFilterNames.ActiveAnomalies &&
      isArray(data.filter.values) &&
      data.filter.values.includes(DashboardActiveAnomaliesEnum.Warning)
    ) {
      const installationsWithWarningAnomaly: DashboardInstallationData[] = demoInstallations.filter(
        (installation: DashboardInstallationData) =>
          installationsWithAnomalies.some(
            (item) =>
              installation.id === item.installationId &&
              item.type === DashboardActiveAnomaliesEnum.Warning
          )
      );
      return of({ data: installationsWithWarningAnomaly }).pipe(delay(800));
    }

    if (data.filter.filterName === DashboardInstallationsFilterNames.PaidPackages) {
      const installationsWithoutHEMS: DashboardInstallationData[] = demoInstallations.filter(
        (installation: DashboardInstallationData) =>
          installationsWithPaidPackages.some((id: number) => installation.id === id)
      );
      return of({ data: installationsWithoutHEMS }).pipe(delay(800));
    }

    return of({ data: [] });
  }

  countInstallationsForFilters(
    bodyData: DashboardInstallationsSumsPayload[]
  ): Observable<GenericDataResponse<DashboardInstallationsSumsResponse[]>> {
    let response: DashboardInstallationsSumsResponse[] = [];
    bodyData.forEach((data) => {
      switch (data.name) {
        case 'installationsWithoutSRC':
          response.push({
            name: data.name,
            value: 10,
          });
          break;
        case 'contractsEndingSoon':
          response.push({
            name: data.name,
            value: 3,
          });
          break;
        case 'installationsWithAnomalies-all':
        case 'installationsWithAnomalies-warning':
          response.push({
            name: data.name,
            value: 2,
          });
          break;
        case 'lastConnectedSystems-last7Days':
        case 'lastConnectedSystems-last30Days':
        case 'lastConnectedSystems-lastYear':
          response.push({
            name: data.name,
            value: 1,
          });
          break;
        case 'lastConnectedSystems-yesterday':
        case 'lastConnectedSystems-today':
        case 'installationsWithAnomalies-critical':
        case 'installationsWithAnomalies-optimization':
        default:
          response.push({
            name: data.name,
            value: 0,
          });
          break;
      }
    });

    return of({ data: response });
  }

  private fetchInstallationsBasedOnStatus(
    filter: DashboardInstallationsFilterPayload
  ): Observable<GenericDataResponse<DashboardInstallationData[]>> {
    switch (filter.values[0]) {
      default:
        return of({ data: demoInstallations });

      case DashboardInstallationsStatusEnum.Error:
        return of({ data: demoInstallations }).pipe(
          map((response: GenericDataResponse<DashboardInstallationData[]>) => {
            const filteredInstallations: DashboardInstallationData[] = response.data.filter(
              (installation: DashboardInstallationData): boolean => {
                const isAggregatedStatusError =
                  installation.aggregatedStatus === AggregatedStatus.Error;
                return filter.modifier === DashboardTileType.InstallationsWithFault
                  ? isAggregatedStatusError && !installation.isSystemLocked
                  : isAggregatedStatusError;
              }
            );
            return { ...response, data: filteredInstallations } as GenericDataResponse<
              DashboardInstallationData[]
            >;
          })
        );

      case DashboardInstallationsStatusEnum.Locked:
        return of({ data: demoInstallations }).pipe(
          map((response: GenericDataResponse<DashboardInstallationData[]>) => {
            const filteredInstallations: DashboardInstallationData[] = response.data.filter(
              (installation: DashboardInstallationData): boolean =>
                installation.aggregatedStatus === AggregatedStatus.Error &&
                installation.isSystemLocked
            );
            return { ...response, data: filteredInstallations } as GenericDataResponse<
              DashboardInstallationData[]
            >;
          })
        );

      case DashboardInstallationsStatusEnum.Maintenance:
        return of({ data: demoInstallations }).pipe(
          map((response: GenericDataResponse<DashboardInstallationData[]>) => {
            const filteredInstallations: DashboardInstallationData[] = response.data.filter(
              (installation: DashboardInstallationData): boolean =>
                installation.aggregatedStatus === AggregatedStatus.Maintenance
            );

            return { ...response, data: filteredInstallations } as GenericDataResponse<
              DashboardInstallationData[]
            >;
          })
        );
    }
  }

  getInstallationListFilterButtonData(): Observable<GenericDataResponse<ButtonFilterData[]>> {
    const response: ButtonFilterData[] = [
      {
        name: ButtonFilterDataName.All,
        value: DashboardApiMocks.demoInstallations.length,
      },
      {
        name: ButtonFilterDataName.Error,
        value: DashboardApiMocks.demoInstallations.filter(
          (el) => el.aggregatedStatus === AggregatedStatus.Error
        ).length,
      },
      {
        name: ButtonFilterDataName.Offline,
        value: DashboardApiMocks.demoInstallations.filter(
          (el) => el.aggregatedStatus === AggregatedStatus.Offline
        ).length,
      },
      {
        name: ButtonFilterDataName.Working,
        value: DashboardApiMocks.demoInstallations.filter(
          (el) => el.aggregatedStatus === AggregatedStatus.WorksProperly
        ).length,
      },
      {
        name: ButtonFilterDataName.Maintenance,
        value: DashboardApiMocks.demoInstallations.filter(
          (el) => el.aggregatedStatus === AggregatedStatus.Maintenance
        ).length,
      },
      {
        name: ButtonFilterDataName.Anomalies,
        value: DashboardApiMocks.demoInstallations.filter((el) =>
          el.devices.some((device) => device.anomalies.length)
        ).length,
      },
    ];

    return of({ data: response });
  }

  getSumsData(
    body: GenericSumsFilterDataPayload[]
  ): Observable<GenericDataResponse<ButtonFilterData[]>> {
    let installations = DashboardApiMocks.demoInstallations;
    body.forEach((filter) => {
      installations = DashboardApiMocks.applyFilters(filter.filters, installations);
    });
    return of({
      data: [{ name: ButtonFilterDataName.All, value: installations.length }],
    });
  }

  private getDashboardMockForLandingPage(): Observable<GenericDataResponse<DashboardColumn[]>> {
    const lockedInstallationsData: DashboardInstallationData[] = demoInstallations.filter(
      (installation: DashboardInstallationData) =>
        lockedInstallations.some((id: number) => installation.id === id)
    );
    const lockedInstallationsDataIds: number[] = lockedInstallationsData.map(
      (installation) => installation.id
    );

    return of({
      data: [
        {
          installations: lockedInstallationsData,
          installationIds: lockedInstallationsDataIds,
          id: 58,
          leftNeighbor: 37,
          name: 'Locked',
          selfDefined: false,
          sortOrder: 'asc',
          version: '2ddcfb1c-9659-4fc6-8097-c82b58e8863c',
        } as DashboardColumn,
      ],
    });
  }
}
